import { apiService } from '../api';
import { Enrollment } from '../../types/sessions';

export interface NewStudentIdentityInput {
  name: string;
  fatherName: string;
  fatherPhone: string;
  phone: string;
  address: string;
  contact?: string;
  groupId?: string | null;
  admissionApplicationId?: string | null;
}

export interface InitialEnrollmentInput {
  sessionId: string;
  classId: string;
  sectionInstanceId?: string | null;
  rollNumber?: number;
}

export interface CompositeCreateStudentInput {
  student: NewStudentIdentityInput;
  initialEnrollment: InitialEnrollmentInput;
}

export interface CompositeCreateStudentResult {
  student: { _id: string; name: string };
  enrollment: Enrollment;
}

export interface UpdateStudentIdentityInput {
  name?: string;
  fatherName?: string;
  fatherPhone?: string;
  phone?: string;
  address?: string;
  contact?: string;
  groupId?: string | null;
}

/**
 * The composite endpoint — only path to create a Student (which always comes
 * with an initial Enrollment, no orphan rows possible).
 */
export const studentsApi = {
  createWithEnrollment: async (input: CompositeCreateStudentInput) => {
    const response = await apiService.post<CompositeCreateStudentResult>('/students', input);
    return response.data!;
  },

  bulkCreate: async (input: {
    sessionId: string;
    classId: string;
    sectionInstanceId?: string | null;
    groupId?: string | null;
    rows: Array<{
      name: string;
      fatherName: string;
      fatherPhone: string;
      phone: string;
      address: string;
      contact?: string;
      rollNumber?: number;
    }>;
  }) => {
    const response = await apiService.post<{ created: number; rollNumbers: number[] }>(
      '/students/bulk',
      input
    );
    return response.data!;
  },

  get: async (id: string) => {
    const response = await apiService.get<any>(`/students/${id}`);
    return response.data!;
  },

  updateIdentity: async (id: string, input: UpdateStudentIdentityInput) => {
    const response = await apiService.put<any>(`/students/${id}`, input);
    return response.data!;
  },

  /**
   * Hard delete. Backend returns 409 with `details.block` (counts of
   * attendance / results / letters / fine payments) when the student has
   * any downstream history — caller should surface the alternative path
   * (close enrollment) in that case.
   */
  remove: async (id: string): Promise<void> => {
    await apiService.delete<void>(`/students/${id}`);
  }
};
